import { useState } from "react";
import { Plus, Trash2, Pencil, Plane, ToggleLeft, ToggleRight, CalendarDays, ClipboardPaste, MapPin, Mail, Car, Gauge, Phone, Check, X, Search } from "lucide-react";

const STATUS = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const DISCOUNT_LABELS = { waive_young_driver: "Waive Young Driver Fee", amount_25: "$25 Off", amount_50: "$50 Off" };

export function BookingsPanel({ bookings, updateStatus, resendReceipt }) {
  return (
    <div className="mt-6 bg-white rounded-2xl shadow-card overflow-hidden" data-testid="bookings-panel">
      {bookings.length === 0 ? <p className="text-slate-400 p-8">No bookings yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
              <th className="py-4 px-5">Customer</th><th className="py-4 px-3">Vehicle</th><th className="py-4 px-3">Dates</th><th className="py-4 px-3">Airport</th><th className="py-4 px-3">Total</th><th className="py-4 px-3">Deposit</th><th className="py-4 px-3">Status</th>
            </tr></thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="py-4 px-5"><div className="text-slate-900 font-medium">{b.customer_name}</div><div className="text-slate-400 text-xs">{b.customer_email}</div><div className="text-slate-400 text-xs">{b.customer_phone}</div></td>
                  <td className="py-4 px-3 text-slate-700">{b.car_name}</td>
                  <td className="py-4 px-3 text-slate-500">{b.pickup_date} → {b.return_date} <span className="text-slate-400">({b.days}d)</span>{b.pickup_location && <div className="text-slate-400 text-xs flex items-center gap-1 mt-0.5"><MapPin size={11} /> {b.pickup_location.split(",")[1]?.trim() || b.pickup_location}</div>}{!b.refundable && <div className="text-amber-600 text-xs mt-0.5">Non-refundable</div>}</td>
                  <td className="py-4 px-3"><span className="text-slate-300">{b.airport_dropoff ? "" : "—"}</span>{b.airport_dropoff && <span className="inline-flex items-center gap-1 text-blue-700 text-xs font-semibold"><Plane size={13} /> Logan</span>}</td>
                  <td className="py-4 px-3 text-slate-900 font-medium">${Number(b.amount_due || 0).toFixed(0)}{b.discount_amount > 0 && <div className="text-green-600 text-xs">−${b.discount_amount} {b.discount_code}</div>}</td>
                  <td className="py-4 px-3">
                    {b.payment_status === "paid"
                      ? <span data-testid={`deposit-badge-${b.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">${b.deposit} Paid</span>
                      : <span data-testid={`deposit-badge-${b.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Unpaid</span>}
                  </td>
                  <td className="py-4 px-3">
                    <div className="flex items-center gap-2">
                      <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className={`px-3 py-1.5 text-xs font-semibold capitalize rounded-full border-0 outline-none cursor-pointer ${STATUS_COLORS[b.status] || ""}`}>
                        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => resendReceipt(b.id)} data-testid={`resend-receipt-${b.id}`} title="Resend receipt to customer" className="text-slate-400 hover:text-blue-600 transition-colors"><Mail size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CarsPanel({ cars, onAdd, onImport, onEdit, onManageDates, onToggle, onDelete, onReview }) {
  const pending = cars.filter((c) => c.review_status === "under_review");
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const visible = query
    ? cars.filter((c) => [c.make, c.name, c.host_name, c.category, c.stock_number, c.vin]
        .some((f) => (f || "").toString().toLowerCase().includes(query)))
    : cars;
  return (
    <div className="mt-6" data-testid="cars-panel">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <button onClick={onAdd} data-testid="add-car-btn" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors">
          <Plus size={16} /> Add Car
        </button>
        <button onClick={onImport} data-testid="import-turo-btn" className="flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-700 px-5 py-2.5 rounded-full text-sm font-semibold hover:border-slate-300 transition-colors">
          <ClipboardPaste size={16} /> Import Turo Booking
        </button>
        {pending.length > 0 && (
          <span data-testid="under-review-count" className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2.5 rounded-full text-sm font-semibold">
            {pending.length} host {pending.length === 1 ? "listing" : "listings"} awaiting review
          </span>
        )}
        <div className="relative ml-auto min-w-[240px] flex-1 max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-car-search"
            placeholder="Search by make, model, or host…"
            className="w-full border-2 border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-sm focus:border-blue-400 focus:outline-none"
          />
        </div>
      </div>
      {query && <p className="text-sm text-slate-500 mb-4" data-testid="admin-car-search-count">{visible.length} {visible.length === 1 ? "vehicle" : "vehicles"} match “{q}”</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((c) => {
          const review = c.review_status === "under_review";
          const rejected = c.review_status === "rejected";
          return (
            <div key={c.id} data-testid={`admin-car-${c.id}`} className={`bg-white rounded-2xl shadow-card overflow-hidden ${review ? "ring-2 ring-amber-300" : ""}`}>
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} className={`w-full h-full object-cover ${c.available === false ? "grayscale opacity-70" : ""}`} />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-300"><Car size={40} /></div>
                )}
                {review ? (
                  <span data-testid={`review-badge-${c.id}`} className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700">Under Review</span>
                ) : rejected ? (
                  <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-red-100 text-red-700">Rejected</span>
                ) : (
                  <span className={`absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full ${c.available === false ? "bg-slate-900/85 text-white" : "bg-green-100 text-green-700"}`}>
                    {c.available === false ? "Unavailable" : "Available"}
                  </span>
                )}
                {(c.blocks || []).length > 0 && (
                  <span className="absolute top-3 left-3 text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700">{c.blocks.length} block{c.blocks.length > 1 ? "s" : ""}</span>
                )}
              </div>
              <div className="p-5">
                <p className="label-caps text-blue-600">{c.make} · {c.category}</p>
                <h3 className="font-heading text-lg mt-1 font-semibold">{c.name}</h3>
                <p className="text-slate-900 mt-2 font-medium">${c.weekly_rate}<span className="text-slate-400 text-xs"> /week</span></p>

                {c.host_name && (
                  <div className="mt-3 text-xs text-slate-500 space-y-1 border-t border-slate-100 pt-3">
                    <p className="flex items-center gap-1.5"><Phone size={11} /> {c.host_name} · {c.host_phone}</p>
                    {c.vin && <p className="font-mono">VIN {c.vin}</p>}
                    {c.odometer != null && <p className="flex items-center gap-1.5"><Gauge size={11} /> {Number(c.odometer).toLocaleString()} mi</p>}
                    {c.host_address && <p className="flex items-center gap-1.5"><MapPin size={11} /> {c.host_address}</p>}
                  </div>
                )}

                {review ? (
                  <>
                    <div className="flex gap-3 mt-4">
                      <button onClick={() => onReview(c.id, "approved")} data-testid={`approve-car-${c.id}`} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"><Check size={15} /> Approve</button>
                      <button onClick={() => onReview(c.id, "rejected")} data-testid={`reject-car-${c.id}`} className="flex-1 flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 py-2 rounded-full text-sm font-semibold hover:bg-red-50 transition-colors"><X size={15} /> Reject</button>
                    </div>
                    <div className="flex gap-3 mt-3">
                      <button onClick={() => onEdit(c)} data-testid={`edit-car-${c.id}`} className="flex-1 flex items-center justify-center gap-2 border-2 border-slate-200 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Pencil size={14} /> Edit</button>
                      <button onClick={() => onDelete(c.id)} data-testid={`delete-car-${c.id}`} className="flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 px-3 py-2 rounded-full hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <button onClick={() => onToggle(c)} data-testid={`toggle-availability-${c.id}`} className={`mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-full text-sm font-semibold transition-colors ${c.available === false ? "bg-green-600 text-white hover:bg-green-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                      {c.available === false ? <><ToggleLeft size={16} /> Mark Available</> : <><ToggleRight size={16} /> Mark Unavailable</>}
                    </button>
                    <div className="flex gap-3 mt-3">
                      <button onClick={() => onManageDates(c)} data-testid={`manage-dates-${c.id}`} className="flex-1 flex items-center justify-center gap-2 border-2 border-slate-200 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><CalendarDays size={14} /> Dates</button>
                      <button onClick={() => onEdit(c)} data-testid={`edit-car-${c.id}`} className="flex-1 flex items-center justify-center gap-2 border-2 border-slate-200 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Pencil size={14} /> Edit</button>
                      <button onClick={() => onDelete(c.id)} data-testid={`delete-car-${c.id}`} className="flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 px-3 py-2 rounded-full hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MessagesPanel({ messages }) {
  return (
    <div className="mt-6 space-y-4" data-testid="messages-panel">
      {messages.length === 0 ? <p className="text-slate-400">No messages yet.</p> :
        messages.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className="bg-white rounded-2xl shadow-card p-6">
            <div className="flex justify-between flex-wrap gap-2">
              <div><span className="text-slate-900 font-medium">{m.name}</span> <span className="text-slate-400 text-sm">· {m.email} {m.phone && `· ${m.phone}`}</span></div>
              <span className="text-slate-400 text-xs">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="text-slate-600 mt-3 leading-relaxed">{m.message}</p>
          </div>
        ))}
    </div>
  );
}

export function DiscountsPanel({ discounts, onAdd, onEdit, onToggle, onDelete }) {
  return (
    <div className="mt-6" data-testid="discounts-panel">
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} data-testid="add-discount-btn" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-blue-700 transition-colors"><Plus size={16} /> Add Code</button>
      </div>
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {discounts.length === 0 ? <p className="text-slate-400 p-8">No discount codes yet. Create one to give customers $25/$50 off or to waive the young driver fee.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Code</th><th className="py-4 px-3">Effect</th><th className="py-4 px-3">Uses</th><th className="py-4 px-3">Status</th><th className="py-4 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {discounts.map((d) => (
                  <tr key={d.id} data-testid={`discount-row-${d.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-4 px-5 font-mono font-semibold text-slate-900">{d.code}</td>
                    <td className="py-4 px-3 text-slate-700">{DISCOUNT_LABELS[d.type] || d.type}</td>
                    <td className="py-4 px-3 text-slate-500">{d.uses}{d.max_uses != null ? ` / ${d.max_uses}` : ""}</td>
                    <td className="py-4 px-3">
                      <button onClick={() => onToggle(d)} data-testid={`toggle-discount-${d.id}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${d.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                        {d.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}{d.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="py-4 px-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => onEdit(d)} data-testid={`edit-discount-${d.id}`} className="text-slate-500 hover:text-slate-900"><Pencil size={15} /></button>
                        <button onClick={() => onDelete(d.id)} data-testid={`delete-discount-${d.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SyncBadge({ action }) {
  const map = {
    blocked: "bg-blue-100 text-blue-700",
    unblocked: "bg-green-100 text-green-700",
    already_blocked: "bg-slate-100 text-slate-600",
    no_matching_block: "bg-slate-100 text-slate-600",
    needs_review: "bg-amber-100 text-amber-700",
    verification: "bg-purple-100 text-purple-700",
    ignored: "bg-slate-100 text-slate-500",
  };
  const label = { blocked: "Blocked", unblocked: "Unblocked", already_blocked: "Already blocked",
    no_matching_block: "No match", needs_review: "Needs review", verification: "Verification", ignored: "Ignored" };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${map[action] || "bg-slate-100 text-slate-600"}`}>{label[action] || action}</span>;
}

function SyncSetup({ webhook }) {
  const [copied, setCopied] = useState(false);
  const url = webhook ? `${process.env.REACT_APP_BACKEND_URL}${webhook.path}?token=${webhook.token}` : "";
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="bg-white rounded-2xl shadow-card p-6" data-testid="sync-setup">
      <h3 className="font-heading font-semibold text-lg">Automatic Turo Sync — Setup</h3>
      <p className="text-slate-500 text-sm mt-1">When a car is booked on Turo, the confirmation email is auto-read and those dates are blocked here. Cancellations auto-unblock.</p>
      <ol className="mt-4 space-y-2 text-sm text-slate-700 list-decimal list-inside">
        <li>Create a free <b>Postmark</b> account → add a <b>Server</b> → open the <b>Inbound</b> stream.</li>
        <li>Paste the webhook URL below into Postmark's <b>Inbound Webhook</b> field, and copy the Postmark <b>inbound email address</b> it gives you.</li>
        <li>In Gmail (Rentals@belorentals.com): Settings → <b>Forwarding</b> → add that inbound address, then create a <b>filter</b> for emails from <b>turo.com</b> → "Forward to" it.</li>
        <li>Gmail sends a confirmation — it'll appear below as a <b>Verification</b> event with the code. Enter it in Gmail to finish.</li>
      </ol>
      <div className="mt-4">
        <span className="label-caps text-slate-400 block mb-2">Your Inbound Webhook URL</span>
        <div className="flex gap-2">
          <input readOnly value={url} data-testid="webhook-url" className="belo-input font-mono text-xs" />
          <button onClick={copy} data-testid="copy-webhook" className="shrink-0 bg-slate-900 text-white px-5 rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors">{copied ? "Copied!" : "Copy"}</button>
        </div>
      </div>
    </div>
  );
}

export function SyncPanel({ webhook, syncEvents }) {
  return (
    <div className="mt-6 space-y-6" data-testid="sync-panel">
      <SyncSetup webhook={webhook} />
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading font-semibold">Recent Turo Activity</h3>
          <span className="text-slate-400 text-sm">{syncEvents.length} event{syncEvents.length !== 1 ? "s" : ""}</span>
        </div>
        {syncEvents.length === 0 ? (
          <p data-testid="sync-empty" className="text-slate-400 p-6">No emails processed yet. Once you set up forwarding, Turo bookings will appear here automatically.</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {syncEvents.map((e) => (
              <div key={e.id} data-testid={`sync-event-${e.id}`} className="px-6 py-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <SyncBadge action={e.action} />
                    {e.car_name && <span className="text-slate-900 font-medium text-sm">{e.car_name}</span>}
                    {e.start && e.end && <span className="text-slate-500 text-sm">{e.start} → {e.end}</span>}
                  </div>
                  <p className="text-slate-500 text-sm mt-1 truncate">{e.subject || e.detail}</p>
                  {e.detail && <p className="text-slate-400 text-xs mt-0.5">{e.detail}</p>}
                </div>
                <span className="text-slate-400 text-xs whitespace-nowrap">{new Date(e.received_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
